/**
 * Command Palette (Cmd+K / Ctrl+K)
 *
 * A unified command palette using cmdk for:
 * - Page navigation (all sidebar pages)
 * - Organization switching
 * - Quick actions (new task, new note, compose email)
 * - AI Search access
 * - Keyboard shortcut help
 *
 * On Triveny.ai the palette is the PRIMARY UX driver — every module is
 * reachable from here, and the page list is filtered to platform-only
 * destinations so healthcare entries don't clutter the search.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow } from 'lucide-react';
import { openMessagingPopout, type MessagingTabSpec } from '@/lib/popout';
import { createPortal } from 'react-dom';
import { config as productConfig } from '@/src/config';
import {
  NAVIGABLE_PAGES as ALL_PAGES,
  TRIVENY_PALETTE_PAGE_IDS,
  WEAVEIN_HIDDEN_TAB_PAGE_IDS,
  navigablePageTarget,
} from '@/lib/navigablePages';
import { useNavigablePages } from '@/hooks/useNavigablePages';

// The page catalog (and getPageDescriptor) lives in lib/navigablePages so the
// tab strips' "+" picker offers exactly the pages this palette can open.
export { getPageDescriptor } from '@/lib/navigablePages';

import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import {
  Search,
  Building,
  ArrowRight,
  Keyboard,
  Plus,
  Mail,
  FileText,
  StickyNote,
  LayoutTemplate,
  Sparkles,
  ArrowRightLeft,
  Home,
  BarChart3,
  Users,
  Calendar,
  Inbox,
  Send,
  Megaphone,
  Globe,
  Target,
  DollarSign,
  Heart,
  MapPin,
  Shield,
  Play,
  Briefcase,
  Brain,
  Bot,
  Award,
  Check,
  Network,
  Store,
  Video,
  BookOpen,
  ClipboardList,
  Receipt,
  CreditCard,
  Code,
  Zap,
  UserCircle,
  HeartPulse,
  UserPlus,
  FolderOpen,
  MessageSquare,
  GitBranch,
  ClipboardCheck,
  ShieldCheck,
  FormInput,
  Library,
  Database,
  ArrowLeftRight,
  MessageCircle,
  Radio,
  Bell,
  Eye,
  Headphones,
  Cog,
  UserCog,
  ScanLine,
  Workflow,
  LayoutDashboard,
  ListTodo,
  TrendingUp,
  PlayCircle,
  Settings,
  Share2,
  Phone,
  Gift,
  AlertTriangle,
  Printer,
  ChevronRight,
  Upload,
  Building2,
  Scale,
} from 'lucide-react';
import { formatShortcutKeys } from '@/contexts/KeyboardShortcutContext';
import { organizationsAPI, type UserOrganization, getActiveCboId } from '@/services/api/organizations';


// ── Tab entries within pages — searchable from the command palette ──
// When selected, the palette navigates to the parent page and sets the tab.
export const ALL_TABS: { pageId: string; tabValue: string; label: string; pageLabel: string; group: string; icon: React.ElementType; keywords?: string }[] = [
  // Customer Support
  { pageId: 'platform-support', tabValue: 'tickets', label: 'Tickets', pageLabel: 'Customer Support', group: 'Customer Support', icon: Headphones, keywords: 'support issues help desk' },
  { pageId: 'platform-support', tabValue: 'ai-chat', label: 'AI Chatbot', pageLabel: 'Customer Support', group: 'Customer Support', icon: Sparkles, keywords: 'bot assistant' },
  { pageId: 'platform-support', tabValue: 'csat', label: 'CSAT', pageLabel: 'Customer Support', group: 'Customer Support', icon: Award, keywords: 'satisfaction score' },
  { pageId: 'platform-support', tabValue: 'surveys', label: 'Surveys', pageLabel: 'Customer Support', group: 'Customer Support', icon: ClipboardList, keywords: 'feedback forms' },
  { pageId: 'platform-support', tabValue: 'knowledge-base', label: 'Knowledge Base', pageLabel: 'Customer Support', group: 'Customer Support', icon: BookOpen, keywords: 'articles docs faq' },
  { pageId: 'platform-support', tabValue: 'forums', label: 'Forums', pageLabel: 'Customer Support', group: 'Customer Support', icon: MessageSquare, keywords: 'community discussion' },
  { pageId: 'platform-support', tabValue: 'onboarding', label: 'Onboarding', pageLabel: 'Customer Support', group: 'Customer Support', icon: UserPlus, keywords: 'getting started welcome' },
  { pageId: 'platform-support', tabValue: 'health-scores', label: 'Health Scores', pageLabel: 'Customer Support', group: 'Customer Support', icon: HeartPulse, keywords: 'customer health risk' },
  { pageId: 'platform-support', tabValue: 'assignment', label: 'Assignment', pageLabel: 'Customer Support', group: 'Customer Support', icon: Users, keywords: 'routing agent team' },
  { pageId: 'platform-support', tabValue: 'analytics', label: 'Analytics', pageLabel: 'Customer Support', group: 'Customer Support', icon: BarChart3, keywords: 'metrics reports' },
  { pageId: 'platform-support', tabValue: 'session-replays', label: 'Session Replays', pageLabel: 'Customer Support', group: 'Customer Support', icon: Eye, keywords: 'recordings playback' },

  // Organization Settings
  { pageId: 'org-settings', tabValue: 'branding', label: 'Branding', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Cog, keywords: 'logo colors theme' },
  { pageId: 'org-settings', tabValue: 'providers', label: 'Communications', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: MessageSquare, keywords: 'email sms whatsapp' },
  { pageId: 'org-settings', tabValue: 'meetings', label: 'Meeting Providers', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Video, keywords: 'zoom teams calendar' },
  { pageId: 'org-settings', tabValue: 'productivity', label: 'Integrations', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Share2, keywords: 'connect apps' },
  { pageId: 'org-settings', tabValue: 'phones', label: 'Phone Numbers', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Phone, keywords: 'twilio provisioning' },
  { pageId: 'org-settings', tabValue: 'qrcodes', label: 'QR Codes', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: ScanLine, keywords: 'scan link' },
  { pageId: 'org-settings', tabValue: 'security', label: 'Security', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Shield, keywords: 'password mfa' },
  { pageId: 'org-settings', tabValue: 'employer_setup', label: 'Employer Setup', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Briefcase },
  { pageId: 'org-settings', tabValue: 'employer', label: 'Employer Programs', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: Briefcase },
  { pageId: 'org-settings', tabValue: 'mail', label: 'Mail / Letters', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: FileText, keywords: 'postal return address postgrid lob print mail' },
  { pageId: 'org-settings', tabValue: 'billing', label: 'Billing', pageLabel: 'Organization Settings', group: 'Organization Settings', icon: CreditCard },

  // Developer Portal
  { pageId: 'developer-portal', tabValue: 'overview', label: 'Overview', pageLabel: 'Developer Portal', group: 'Developer Portal', icon: Code, keywords: 'api getting started' },
  { pageId: 'developer-portal', tabValue: 'docs', label: 'Documentation', pageLabel: 'Developer Portal', group: 'Developer Portal', icon: BookOpen, keywords: 'api reference endpoints' },
  { pageId: 'developer-portal', tabValue: 'sandbox', label: 'Sandbox & API Keys', pageLabel: 'Developer Portal', group: 'Developer Portal', icon: Code, keywords: 'test environment keys' },
  { pageId: 'developer-portal', tabValue: 'usage', label: 'Usage & Analytics', pageLabel: 'Developer Portal', group: 'Developer Portal', icon: BarChart3, keywords: 'api calls metrics' },

  // Document Processing (Billing)
  { pageId: 'document-processing', tabValue: 'member-profiles', label: 'Member Profile Creation', pageLabel: 'Document Processing', group: 'Document Processing', icon: UserCircle, keywords: 'insurance card scanning' },
  { pageId: 'document-processing', tabValue: 'master-claims', label: 'Master Claims', pageLabel: 'Document Processing', group: 'Document Processing', icon: FileText, keywords: 'visit line items' },
  { pageId: 'document-processing', tabValue: 'full-claim-entry', label: 'Full Claim Entry', pageLabel: 'Document Processing', group: 'Document Processing', icon: ScanLine, keywords: 'ocr extraction' },

  // Member Incentives
  { pageId: 'member-incentives', tabValue: 'overview', label: 'Overview', pageLabel: 'Member Incentives', group: 'Member Incentives', icon: Gift, keywords: 'rewards summary' },
  { pageId: 'member-incentives', tabValue: 'card', label: 'My Card', pageLabel: 'Member Incentives', group: 'Member Incentives', icon: CreditCard, keywords: 'debit card balance' },
  { pageId: 'member-incentives', tabValue: 'payouts', label: 'Payment History', pageLabel: 'Member Incentives', group: 'Member Incentives', icon: Receipt, keywords: 'transactions' },

  // Member Communications
  { pageId: 'member-communications', tabValue: 'unified', label: 'Unified Timeline', pageLabel: 'Member Communications', group: 'Member Communications', icon: MessageSquare, keywords: 'all channels' },
  { pageId: 'member-communications', tabValue: 'classic', label: 'Classic View', pageLabel: 'Member Communications', group: 'Member Communications', icon: Inbox, keywords: 'traditional' },

  // Employee Portal – Benefits
  { pageId: 'employee-benefits', tabValue: 'programs', label: 'Program Benefits', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: Gift },
  { pageId: 'employee-benefits', tabValue: 'claims', label: 'Claims & EOBs', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: FileText },
  { pageId: 'employee-benefits', tabValue: 'accounts', label: 'HSA / FSA', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: DollarSign },
  { pageId: 'employee-benefits', tabValue: 'id-cards', label: 'ID Cards', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: CreditCard },
  { pageId: 'employee-benefits', tabValue: 'dependents', label: 'Dependents', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: Users },
  { pageId: 'employee-benefits', tabValue: 'providers', label: 'Find Providers', pageLabel: 'Employee Benefits', group: 'Employee Portal', icon: MapPin },

  // Employee Portal – Incentives
  { pageId: 'employee-incentives', tabValue: 'overview', label: 'Overview', pageLabel: 'Employee Incentives', group: 'Employee Portal', icon: Gift },
  { pageId: 'employee-incentives', tabValue: 'card', label: 'My Card', pageLabel: 'Employee Incentives', group: 'Employee Portal', icon: CreditCard },
  { pageId: 'employee-incentives', tabValue: 'history', label: 'Payment History', pageLabel: 'Employee Incentives', group: 'Employee Portal', icon: Receipt },

  // Employee Portal – Messages
  { pageId: 'employee-messages', tabValue: 'inbox', label: 'Inbox', pageLabel: 'Employee Messages', group: 'Employee Portal', icon: Inbox },
  { pageId: 'employee-messages', tabValue: 'sent', label: 'Sent', pageLabel: 'Employee Messages', group: 'Employee Portal', icon: Send },
  { pageId: 'employee-messages', tabValue: 'benefits', label: 'Benefits', pageLabel: 'Employee Messages', group: 'Employee Portal', icon: Gift },
  { pageId: 'employee-messages', tabValue: 'care_team', label: 'Care Team', pageLabel: 'Employee Messages', group: 'Employee Portal', icon: Heart },
  { pageId: 'employee-messages', tabValue: 'employer', label: 'HR', pageLabel: 'Employee Messages', group: 'Employee Portal', icon: Briefcase },

  // Workflow Templates — route to template list (editing requires a selected template)
  { pageId: 'workflows', tabValue: 'templates', label: 'Templates', pageLabel: 'Agent Workflow Templates', group: 'Workflow Templates', icon: ClipboardList },
  { pageId: 'workflow-instances', tabValue: 'instances', label: 'Instances', pageLabel: 'Workflow Center', group: 'Workflow Templates', icon: Play },

  // Program & Provider Import — sub-tabs of the import manager (page id: admin)
  { pageId: 'admin', tabValue: 'providers', label: 'Provider Import', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Upload, keywords: 'cms nppes hrsa providers import healthcare' },
  { pageId: 'admin', tabValue: 'cbo-import', label: 'CBO Import', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Building2, keywords: 'community based organization import' },
  { pageId: 'admin', tabValue: 'program-import', label: 'Program Import', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: FileText, keywords: 'program intelligence cms hrsa state filings import' },
  { pageId: 'admin', tabValue: 'intent-sources', label: 'Intent Sources', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Radio, keywords: 'intent signal sources' },
  { pageId: 'admin', tabValue: 'ml-data', label: 'ML Data Sources', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Brain, keywords: 'machine learning feature store data sources' },
  { pageId: 'admin', tabValue: 'plan-comparison', label: 'Plan Comparison', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Scale, keywords: 'plan comparison data' },
  { pageId: 'admin', tabValue: 'enrichment', label: 'Provider Enrichment', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Sparkles, keywords: 'ai enrichment web scraping social media providers cbos programs' },
  { pageId: 'admin', tabValue: 'etl-pipeline', label: 'ETL Pipeline', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: Play, keywords: 'etl job history scheduled imports orchestration geocoding' },
  { pageId: 'admin', tabValue: 'analytics', label: 'Import Analytics', pageLabel: 'Program & Provider Import', group: 'Program & Provider Import', icon: BarChart3, keywords: 'cost trends smart recommendations import analytics' },
];

// ── Registry integrity guard (recurrence prevention) ──────────────────────
// ALL_TABS is a hand-maintained mirror of app.tsx's activeTab
// route table, so duplicate ids slip in silently — two rows that navigate to
// the same tab (e.g. a stale copy left behind after a rename, or a paste with
// a changed label but the same id). Those render as duplicate / mislabeled
// picker rows. Fail loudly at module load so it never ships:
//   • duplicate ALL_TABS `pageId::tabValue`    → always a bug (throws in dev)
// (The page catalog runs the same guard on its ids in lib/navigablePages.)
// In production builds (import.meta.env.DEV === false) we downgrade to
// console.error so a stray dup never white-screens the app for a user.
function assertPaletteRegistryIntegrity(): void {
  const findDupes = <T,>(items: T[], keyOf: (item: T) => string): string[] => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const item of items) {
      const key = keyOf(item);
      if (seen.has(key)) dupes.add(key);
      seen.add(key);
    }
    return [...dupes];
  };

  const dupTabKeys = findDupes(ALL_TABS, t => `${t.pageId}::${t.tabValue}`);

  const errors: string[] = [];
  if (dupTabKeys.length) errors.push(`duplicate ALL_TABS pageId::tabValue: ${dupTabKeys.join(', ')}`);

  const isDev = import.meta.env.DEV;
  if (errors.length) {
    const msg = `[CommandPalette] registry integrity error — ${errors.join('; ')}`;
    if (isDev) throw new Error(msg);
    // eslint-disable-next-line no-console
    console.error(msg);
  }
}
assertPaletteRegistryIntegrity();

interface SidebarPage {
  id: string;
  label: string;
  group: string;
}

interface ExtendedOrganization extends UserOrganization {
  is_sponsor?: boolean;
  sponsor_id?: number;
  sponsor_type?: string;
  is_employer?: boolean;
  employer_id?: number;
  employer_name?: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: string) => void;
  onOpenAISearch: () => void;
  onComposeEmail?: () => void;
  /**
   * Open the Copilot. Receives whatever the user had typed, so a question they
   * started in the palette is not lost on the way — the palette is where they
   * are already typing, and re-typing it is exactly the friction that makes
   * people not bother.
   */
  onOpenCopilot?: (question?: string) => void;
  userRole?: string;
}

type PaletteMode = 'default' | 'navigation' | 'orgs';

export function CommandPalette({
  isOpen,
  onClose,
  onNavigate,
  onOpenAISearch,
  onComposeEmail,
  onOpenCopilot,
  userRole,
}: CommandPaletteProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PaletteMode>('default');
  // What the user has typed. cmdk owns filtering; we mirror the value only so
  // "Ask Copilot" can carry the half-typed question across instead of dropping it.
  const [search, setSearch] = useState('');
  const [organizations, setOrganizations] = useState<ExtendedOrganization[]>([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [sidebarPages, setSidebarPages] = useState<SidebarPage[]>([]);
  const activeOrgId = getActiveCboId();
  // The pages this user may be offered: product / role / org / pilot /
  // capability gating, shared with the tab strips' "+" picker.
  const catalogPages = useNavigablePages(activeOrgId, userRole);

  // Listen for access-filtered navigation pages from SidebarNavigation
  useEffect(() => {
    const handler = (e: Event) => {
      const pages = (e as CustomEvent).detail as SidebarPage[];
      if (pages) setSidebarPages(pages);
    };
    window.addEventListener('sidebar-nav-pages', handler);
    return () => window.removeEventListener('sidebar-nav-pages', handler);
  }, []);

  // Reset mode when palette opens
  useEffect(() => {
    if (isOpen) {
      setMode('default');
      // Clear the mirrored query too, or reopening the palette would show the
      // previous session's text in the "Ask Copilot" label.
      setSearch('');
    }
  }, [isOpen]);

  // Listen for external mode switch (e.g., Alt+P opens directly to orgs)
  useEffect(() => {
    const handler = (e: Event) => {
      const newMode = (e as CustomEvent).detail as PaletteMode;
      if (newMode) setMode(newMode);
    };
    window.addEventListener('command-palette-mode', handler);
    return () => window.removeEventListener('command-palette-mode', handler);
  }, []);

  // Load orgs when switching to org mode
  useEffect(() => {
    if (mode === 'orgs' && !orgsLoaded) {
      loadOrganizations();
    }
  }, [mode, orgsLoaded]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (mode !== 'default') {
          setMode('default');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, mode, onClose]);

  const loadOrganizations = async () => {
    try {
      const orgs = await organizationsAPI.getMyOrganizations();
      const extendedOrgs: ExtendedOrganization[] = orgs.map(org => ({
        ...org,
        is_sponsor: org.organization?.is_sponsor || false,
        sponsor_id: org.organization?.sponsor_id,
        sponsor_type: org.organization?.sponsor_type,
        is_employer: !!(org.organization as any)?.employer_id ||
          (org.organization?.name?.toLowerCase() || '').includes('employer'),
        employer_id: (org.organization as any)?.employer_id,
        employer_name: (org.organization as any)?.employer_name,
      }));
      setOrganizations(extendedOrgs);
      setOrgsLoaded(true);
    } catch {
      // Silently fail
    }
  };

  const handleOrgSwitch = async (orgId: number) => {
    try {
      const org = organizations.find(o => o.cbo_id === orgId);
      await organizationsAPI.setActiveOrganization(orgId);

      if (org?.is_sponsor && org.sponsor_id) {
        localStorage.setItem('active_sponsor_id', String(org.sponsor_id));
      } else {
        localStorage.removeItem('active_sponsor_id');
      }

      if (org?.is_employer && org.employer_id) {
        localStorage.setItem('active_employer_id', String(org.employer_id));
      } else {
        localStorage.removeItem('active_employer_id');
      }

      localStorage.removeItem('activeTab');
      onClose();
      window.location.href = window.location.origin;
    } catch {
      // Error handled silently
    }
  };

  // Track the last 5 destinations the user navigated to from the palette.
  // Persisted to localStorage so a fresh page load can render "Recent"
  // without an empty placeholder. Triveny-only — WeaveIn keeps its
  // current behavior.
  const TRIVENY_RECENT_KEY = 'triveny_palette_recent';
  const TRIVENY_RECENT_MAX = 5;
  const recordRecent = (tabId: string) => {
    if (!productConfig.isTriveny) return;
    try {
      const raw = localStorage.getItem(TRIVENY_RECENT_KEY);
      const prev: string[] = raw ? JSON.parse(raw) : [];
      const next = [tabId, ...prev.filter(t => t !== tabId)].slice(0, TRIVENY_RECENT_MAX);
      localStorage.setItem(TRIVENY_RECENT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const handleSelect = useCallback((action: string) => {
    switch (action) {
      case '__ai_search':
        onClose();
        onOpenAISearch();
        break;
      case '__copilot':
        onClose();
        // Pass the typed text through. cmdk keeps matching "Ask Copilot" while
        // the user types a real question, so by the time they hit Enter the
        // input usually holds the thing they actually want to ask.
        onOpenCopilot?.(search.trim() || undefined);
        break;
      // ── Triveny action commands ──
      // These don't navigate to a fresh tab on their own; they navigate
      // to the relevant tab AND emit a custom event the destination page
      // can listen for to open its create-modal / compose state.
      case '__tv_run_campaign':
        onClose();
        onNavigate('platform-marketing');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-campaign' })), 100);
        break;
      case '__tv_new_deal':
        onClose();
        onNavigate('platform-sales');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-deal' })), 100);
        break;
      case '__tv_new_contact':
        onClose();
        onNavigate('platform-contacts');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-contact' })), 100);
        break;
      case '__tv_new_ticket':
        onClose();
        onNavigate('platform-support');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-ticket' })), 100);
        break;
      case '__tv_new_page':
        onClose();
        onNavigate('platform-web-presence');
        setTimeout(() => window.dispatchEvent(new CustomEvent('triveny-action', { detail: 'new-page' })), 100);
        break;
      case '__shortcuts':
        onClose();
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('keyboard-shortcut', { detail: { action: 'toggle-help' } }));
        }, 50);
        break;
      case '__compose':
        onClose();
        // Open the inbox modality picker via custom event
        window.dispatchEvent(new CustomEvent('v2-quick-action', { detail: { action: 'compose' } }));
        break;
      case '__new_task':
        onClose();
        window.dispatchEvent(new CustomEvent('v2-quick-action', { detail: { action: 'new-task' } }));
        break;
      case '__new_note':
        onClose();
        window.dispatchEvent(new CustomEvent('v2-quick-action', { detail: { action: 'new-note' } }));
        break;
      case '__REMOVED_new_task': {
        // Old prompt-based handler kept as reference
        const taskTitle = window.prompt('Task title:');
        if (taskTitle) {
          const token = localStorage.getItem('auth_token');
          const cboId = localStorage.getItem('active_cbo_id');
          fetch('/api/tasks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId || '' },
            body: JSON.stringify({ title: taskTitle, task_type: 'task', status: 'not_started', priority: 'medium', member_id: 0, is_archived: false, progress_percentage: 0 }),
          }).then(() => { onClose(); }).catch(console.error);
        }
        break;
      }
      case '__REMOVED_new_note': {
        const noteContent = window.prompt('Note content:');
        if (noteContent) {
          const token = localStorage.getItem('auth_token');
          const cboId = localStorage.getItem('active_cbo_id');
          fetch('/api/case-notes/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId || '' },
            body: JSON.stringify({ content: noteContent, note_type: 'general' }),
          }).then(() => { onClose(); }).catch(console.error);
        }
        break;
      }
      case '__mode_nav':
        setMode('navigation');
        break;
      case '__mode_orgs':
        setMode('orgs');
        break;
      default:
        // Check if this is a subtab navigation (format: "pageId::tabValue")
        if (action.includes('::')) {
          const [pageId, tabValue] = action.split('::');
          localStorage.setItem('pending_subtab', tabValue);
          onClose();
          recordRecent(pageId);
          onNavigate(pageId);
          // Also fire event in case the page is already mounted
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('navigate-to-subtab', { detail: tabValue }));
          }, 100);
        } else {
          // Navigation — action is the tab ID.
          // Triveny has no healthcare CBO directory; its "Organizations" is the
          // operator sign-up directory at org-settings (TrivenyWorkspaceAdmin).
          // Route the palette's cbo-management entry there so both surfaces
          // show the identical list.
          const target = navigablePageTarget(action, productConfig.isTriveny);
          onClose();
          recordRecent(target);
          onNavigate(target);
        }
        break;
    }
  }, [onClose, onNavigate, onOpenAISearch, onComposeEmail, onOpenCopilot, search]);

  // Load recent destinations from localStorage. Refreshes when the
  // palette opens so subsequent opens reflect what just happened.
  const [recentTabs, setRecentTabs] = useState<string[]>([]);
  useEffect(() => {
    if (!productConfig.isTriveny || !isOpen) return;
    try {
      const raw = localStorage.getItem(TRIVENY_RECENT_KEY);
      setRecentTabs(raw ? JSON.parse(raw) : []);
    } catch {
      setRecentTabs([]);
    }
  }, [isOpen]);

  // The palette always shows every page from ALL_PAGES so users can search by
  // label/keyword regardless of sidebar filtering. When the sidebar emits
  // org-aware labels/groups, prefer those for matching pages — except where the
  // palette's own grouping is intentional (e.g. the Revenue Cycle & Claims
  // Management consolidation, which spans multiple sidebar sub-sections).
  const PRESERVE_PALETTE_GROUP = React.useMemo(
    () =>
      new Set(
        ALL_PAGES
          .filter(p => p.group === 'Revenue Cycle & Claims Management')
          .map(p => p.id)
      ),
    []
  );
  const activePagesSource = React.useMemo(() => {
    const sidebarById = new Map(sidebarPages.map(p => [p.id, p]));
    // Product / role / org / pilot / capability gating: lib/navigablePages.
    return catalogPages
      .map(page => {
        const sidebar = sidebarById.get(page.id);
        if (!sidebar) return page;
        // Take sidebar's org-aware label, but keep the palette's group when it's
        // an intentional cross-cutting section.
        const groupToUse = PRESERVE_PALETTE_GROUP.has(page.id) ? page.group : sidebar.group;
        return { ...page, label: sidebar.label, group: groupToUse };
      });
  }, [sidebarPages, PRESERVE_PALETTE_GROUP, catalogPages]);

  // Filter ALL_TABS the same way as ALL_PAGES — on Triveny, only show tabs
  // whose parent pageId is in the Triveny allowlist. This prevents
  // healthcare-specific tab quick-actions (e.g. RevOps' billing tabs that
  // are wired to /api/cbos endpoints) from appearing in the palette.
  const activeTabsSource = React.useMemo(() => {
    if (!productConfig.isTriveny) {
      // On WeaveIn, hide Triveny-only twin tabs so the picker doesn't show a
      // duplicate "Roles & Permissions" (platform-roles is the Triveny twin of
      // access-control and only renders under config.isTriveny).
      return ALL_TABS.filter(t => !WEAVEIN_HIDDEN_TAB_PAGE_IDS.has(t.pageId));
    }
    // org-settings SUBTABS (Branding / Communications / Phone Numbers / Billing /
    // …) belong to the healthcare OrganizationSettingsPage and render the
    // WeaveIn-branded org page. On Triveny the org surface is the unified
    // TrivenyWorkspaceAdmin (reached via the "Organizations" page), so drop
    // these WeaveIn quick-actions entirely.
    return ALL_TABS.filter(t => TRIVENY_PALETTE_PAGE_IDS.has(t.pageId) && t.pageId !== 'org-settings');
  }, []);

  // Group pages by group name for navigation mode
  const groupedPages = React.useMemo(() => {
    const groups: Record<string, { id: string; label: string; group: string; icon?: React.ElementType }[]> = {};
    for (const page of activePagesSource) {
      if (!groups[page.group]) groups[page.group] = [];
      // Match icon from static list if available
      const staticPage = ALL_PAGES.find(p => p.id === page.id);
      groups[page.group].push({
        ...page,
        icon: staticPage?.icon || ArrowRight,
      });
    }
    return groups;
  }, [activePagesSource]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const modKey = isMac ? '\u2318' : 'Ctrl';

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <Command
          className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2"
          loop
        >
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={
                mode === 'orgs'
                  ? 'Search organizations...'
                  : mode === 'navigation'
                  ? 'Search pages and tabs...'
                  : 'Type a command or search...'
              }
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50 border-0 ring-0 focus:ring-0"
              autoFocus
            />
            {mode !== 'default' && (
              <button
                onClick={() => setMode('default')}
                className="ml-2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
              >
                Back
              </button>
            )}
            <kbd className="ml-2 pointer-events-none inline-flex h-5 items-center gap-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-1.5 font-mono text-[10px] font-medium text-gray-500 dark:text-gray-400">
              ESC
            </kbd>
          </div>

          <CommandList className="max-h-[400px] overflow-y-auto p-1">
            <CommandEmpty className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No results found.
            </CommandEmpty>

            {/* ── Default mode ── */}
            {mode === 'default' && (
              <>
                {/* Triveny: Recent destinations (top 5). Single keystroke to repeat
                    the user's last action. Hidden when no history exists. */}
                {productConfig.isTriveny && recentTabs.length > 0 && (
                  <>
                    <CommandGroup heading="Recent">
                      {recentTabs.map((tabId) => {
                        const page = ALL_PAGES.find((p) => p.id === tabId);
                        if (!page) return null;
                        const Icon = page.icon || ArrowRight;
                        return (
                          <CommandItem
                            key={tabId}
                            onSelect={() => handleSelect(tabId)}
                            className="cursor-pointer"
                            value={`${page.label} ${page.group} recent`}
                          >
                            <Icon className="mr-2 h-4 w-4 text-gray-500" />
                            <span>{page.label}</span>
                            <span className="ml-auto text-xs text-gray-400">{page.group}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}

                {/* Triveny: action commands — these CREATE / DO, not just navigate.
                    Destination page listens for `triveny-action` to open its
                    create-modal. */}
                {productConfig.isTriveny && (
                  <>
                    <CommandGroup heading="Triveny Actions">
                      {/* Go to Home — primary anchor to the full-bleed launcher.
                          Pinned at the top so users can always escape back. */}
                      <CommandItem onSelect={() => handleSelect('triveny-home')} className="cursor-pointer">
                        <Home className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Go to Home</span>
                        <CommandShortcut>{formatShortcutKeys('alt+h')}</CommandShortcut>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('platform-dashboard')} className="cursor-pointer">
                        <Calendar className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Go to My Day</span>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('workspace-messaging')} className="cursor-pointer">
                        <Mail className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Open Inbox</span>
                        <CommandShortcut>{formatShortcutKeys('alt+i')}</CommandShortcut>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('__tv_run_campaign')} className="cursor-pointer">
                        <Send className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Run a campaign</span>
                        <CommandShortcut>{formatShortcutKeys('alt+shift+c')}</CommandShortcut>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('__tv_new_deal')} className="cursor-pointer">
                        <Target className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Create a deal</span>
                        <CommandShortcut>{formatShortcutKeys('alt+shift+d')}</CommandShortcut>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('__tv_new_contact')} className="cursor-pointer">
                        <Users className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>New contact</span>
                        <CommandShortcut>{formatShortcutKeys('alt+shift+u')}</CommandShortcut>
                      </CommandItem>
                      {/* Compose message removed — its v2-quick-action dispatch
                          is gated by isV2Enabled which is force-false on Triveny,
                          so the row was a silent no-op. Restore once a
                          Triveny-side composer modal is wired up. */}
                      <CommandItem onSelect={() => handleSelect('__tv_new_ticket')} className="cursor-pointer">
                        <Headphones className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>Open a support ticket</span>
                        <CommandShortcut>{formatShortcutKeys('alt+shift+s')}</CommandShortcut>
                      </CommandItem>
                      <CommandItem onSelect={() => handleSelect('__tv_new_page')} className="cursor-pointer">
                        <LayoutTemplate className="mr-2 h-4 w-4 text-cyan-600" />
                        <span>New landing page</span>
                        <CommandShortcut>{formatShortcutKeys('alt+shift+l')}</CommandShortcut>
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}

                {/* Quick Actions */}
                <CommandGroup heading="Quick Actions">
                  {onOpenCopilot && !productConfig.isTriveny && (
                    <CommandItem
                      onSelect={() => handleSelect('__copilot')}
                      className="cursor-pointer"
                      // cmdk filters on this string, not on the rendered label,
                      // so the entry survives while the user types a question
                      // that matches none of the page names.
                      value="ask copilot assistant question help how do i where is"
                    >
                      <Bot className="mr-2 h-4 w-4 text-indigo-500" />
                      <span>
                        {search.trim() ? `Ask Copilot: "${search.trim()}"` : 'Ask Copilot'}
                      </span>
                      <CommandShortcut>{modKey}+J</CommandShortcut>
                    </CommandItem>
                  )}
                  <CommandItem onSelect={() => handleSelect('__ai_search')} className="cursor-pointer">
                    <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
                    <span>AI Search</span>
                    <CommandShortcut>{modKey}+{isMac ? '\u21E7' : 'Shift'}+K</CommandShortcut>
                  </CommandItem>
                  {!productConfig.isTriveny && (
                    <CommandItem onSelect={() => handleSelect('__compose')} className="cursor-pointer">
                      <Mail className="mr-2 h-4 w-4 text-blue-500" />
                      <span>Compose new message</span>
                      <CommandShortcut>{formatShortcutKeys('alt+n')}</CommandShortcut>
                    </CommandItem>
                  )}
                  {/* Messaging pop-outs: each opens its own window with a tab strip. */}
                  {!productConfig.isTriveny &&
                    ([
                      ['members', t('messagingPopout.palette_inbox', 'Open Inbox in new window'), 'pop out new window inbox member conversations'],
                      ['mailbox', t('messagingPopout.palette_mailbox', 'Open Mailbox in new window'), 'pop out new window mailbox outlook email'],
                    ] as Array<[Exclude<MessagingTabSpec['kind'], 'channel' | 'channels'>, string, string]>).map(([kind, label, keywords]) => (
                      <CommandItem
                        key={`popout-${kind}`}
                        value={`${label} ${keywords}`}
                        onSelect={() => {
                          onClose();
                          void openMessagingPopout({ tabs: [{ kind }] }, 'window').then((r) => {
                            if (!r.ok) console.error('Open in new window failed:', r.error);
                          });
                        }}
                        className="cursor-pointer"
                      >
                        <AppWindow className="mr-2 h-4 w-4 text-blue-500" />
                        <span>{label}</span>
                      </CommandItem>
                    ))}
                  <CommandItem onSelect={() => handleSelect('__new_task')} className="cursor-pointer">
                    <Plus className="mr-2 h-4 w-4 text-green-500" />
                    <span>New task</span>
                    <CommandShortcut>{formatShortcutKeys('alt+t')}</CommandShortcut>
                  </CommandItem>
                  <CommandItem onSelect={() => handleSelect('__new_note')} className="cursor-pointer">
                    <StickyNote className="mr-2 h-4 w-4 text-yellow-500" />
                    <span>New note</span>
                    <CommandShortcut>{formatShortcutKeys('alt+j')}</CommandShortcut>
                  </CommandItem>
                </CommandGroup>

                <CommandSeparator />

                {/* Go To sub-menus */}
                <CommandGroup heading="Go To">
                  <CommandItem onSelect={() => handleSelect('__mode_nav')} className="cursor-pointer">
                    <ArrowRight className="mr-2 h-4 w-4 text-gray-500" />
                    <span>Navigate to page...</span>
                    <CommandShortcut>{formatShortcutKeys('alt+shift+p')}</CommandShortcut>
                  </CommandItem>
                {/* Hide org switcher on Triveny — no platform tenant switcher
                    exists yet (the org switcher hits /api/cbos which 401s for
                    platform JWTs). Restore once a Triveny-side endpoint lands. */}
                {!productConfig.isTriveny && (
                  <CommandItem onSelect={() => handleSelect('__mode_orgs')} className="cursor-pointer">
                    <ArrowRightLeft className="mr-2 h-4 w-4 text-gray-500" />
                    <span>Switch organization...</span>
                    <CommandShortcut>{formatShortcutKeys('alt+p')}</CommandShortcut>
                  </CommandItem>
                )}
                </CommandGroup>

                <CommandSeparator />

                {/* Frequently accessed pages — Triveny gets a different set since
                    Home/Members/Calendar/Claims are healthcare-only. */}
                {productConfig.isTriveny ? (
                  <CommandGroup heading="Navigation">
                    <CommandItem onSelect={() => handleSelect('triveny-home')} className="cursor-pointer" value="Home triveny launcher">
                      <Home className="mr-2 h-4 w-4" />
                      <span>Home</span>
                      <CommandShortcut>{formatShortcutKeys('alt+h')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('platform-dashboard')} className="cursor-pointer" value="My day dashboard">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>My Day</span>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('workspace-messaging')} className="cursor-pointer" value="Inbox messaging">
                      <Inbox className="mr-2 h-4 w-4" />
                      <span>Inbox</span>
                      <CommandShortcut>{formatShortcutKeys('alt+i')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('platform-marketing')} className="cursor-pointer" value="Marketing campaigns">
                      <Send className="mr-2 h-4 w-4" />
                      <span>Marketing</span>
                      <CommandShortcut>{formatShortcutKeys('alt+k')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('platform-sales')} className="cursor-pointer" value="Sales pipeline">
                      <Target className="mr-2 h-4 w-4" />
                      <span>Pipeline</span>
                      <CommandShortcut>{formatShortcutKeys('alt+s')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('platform-contacts')} className="cursor-pointer" value="Audiences contacts">
                      <UserCircle className="mr-2 h-4 w-4" />
                      <span>Audiences</span>
                      <CommandShortcut>{formatShortcutKeys('alt+o')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('platform-marketing-analytics')} className="cursor-pointer" value="Analytics dashboard">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      <span>Analytics</span>
                    </CommandItem>
                  </CommandGroup>
                ) : (
                  <CommandGroup heading="Navigation">
                    <CommandItem onSelect={() => handleSelect('home')} className="cursor-pointer" value="Home dashboard">
                      <Home className="mr-2 h-4 w-4" />
                      <span>Home</span>
                      <CommandShortcut>{formatShortcutKeys('alt+h')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('members')} className="cursor-pointer" value="Members">
                      <UserCircle className="mr-2 h-4 w-4" />
                      <span>Members</span>
                      <CommandShortcut>{formatShortcutKeys('alt+m')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('workspace-messaging')} className="cursor-pointer" value="Inbox email messages member conversations">
                      <Inbox className="mr-2 h-4 w-4" />
                      <span>Inbox</span>
                      <CommandShortcut>{formatShortcutKeys('alt+i')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('calendar')} className="cursor-pointer" value="Calendar scheduling">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>Calendar</span>
                      <CommandShortcut>{formatShortcutKeys('alt+c')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('analytics')} className="cursor-pointer" value="Analytics dashboard">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      <span>Analytics</span>
                      <CommandShortcut>{formatShortcutKeys('alt+a')}</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => handleSelect('billing-claims')} className="cursor-pointer" value="Claims billing">
                      <FileText className="mr-2 h-4 w-4" />
                      <span>Claims</span>
                    </CommandItem>
                  </CommandGroup>
                )}

                <CommandSeparator />

                {/* All pages — searchable in default mode (before Page Tabs so pages match first) */}
                <CommandGroup heading="Pages">
                  {activePagesSource.map(page => {
                    const Icon = ALL_PAGES.find(p => p.id === page.id)?.icon || ArrowRight;
                    return (
                      <CommandItem
                        key={`page-${page.id}`}
                        value={`${page.label} ${page.group} ${(ALL_PAGES.find(p => p.id === page.id) as any)?.keywords || ''}`}
                        onSelect={() => handleSelect(page.id)}
                        className="cursor-pointer"
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        <span>{page.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>

                <CommandSeparator />

                {/* Page tabs — sub-page navigation */}
                <CommandGroup heading="Page Tabs">
                  {activeTabsSource.map(tab => {
                    const Icon = tab.icon || ArrowRight;
                    return (
                      <CommandItem
                        key={`default-${tab.pageId}::${tab.tabValue}`}
                        value={`${tab.label} ${tab.pageLabel} ${tab.group} ${tab.keywords || ''} tab`}
                        onSelect={() => handleSelect(`${tab.pageId}::${tab.tabValue}`)}
                        className="cursor-pointer"
                      >
                        <Icon className="mr-2 h-4 w-4 text-indigo-500" />
                        <span>{tab.pageLabel}</span>
                        <ChevronRight className="mx-1 h-3 w-3 text-gray-400" />
                        <span className="font-medium">{tab.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>

                <CommandSeparator />

                {/* Settings / Help */}
                <CommandGroup heading="Settings">
                  <CommandItem onSelect={() => handleSelect('__shortcuts')} className="cursor-pointer">
                    <Keyboard className="mr-2 h-4 w-4 text-gray-500" />
                    <span>Keyboard shortcuts</span>
                    <CommandShortcut>?</CommandShortcut>
                  </CommandItem>
                </CommandGroup>
              </>
            )}

            {/* ── Navigation mode — pages with their tabs inline ── */}
            {mode === 'navigation' && (
              <>
                {Object.entries(groupedPages).map(([groupName, pages]) => {
                  // Find tabs that belong to pages in this group
                  const groupPageIds = pages.map(p => p.id);
                  const groupTabs = activeTabsSource.filter(t => groupPageIds.includes(t.pageId));
                  // Also include tabs whose group name matches this page group
                  const groupNameTabs = activeTabsSource.filter(t => t.group === groupName && !groupPageIds.includes(t.pageId));
                  const allGroupTabs = [...groupTabs, ...groupNameTabs];

                  return (
                    <CommandGroup key={groupName} heading={groupName}>
                      {pages.map(page => {
                        const Icon = page.icon || ArrowRight;
                        const staticPage = ALL_PAGES.find(p => p.id === page.id);
                        return (
                          <CommandItem
                            key={page.id}
                            value={`${page.label} ${groupName} ${staticPage?.keywords || ''}`}
                            onSelect={() => handleSelect(page.id)}
                            className="cursor-pointer"
                          >
                            <Icon className="mr-2 h-4 w-4" />
                            <span>{page.label}</span>
                          </CommandItem>
                        );
                      })}
                      {allGroupTabs.map(tab => {
                        const Icon = tab.icon || ArrowRight;
                        return (
                          <CommandItem
                            key={`nav-${tab.pageId}::${tab.tabValue}`}
                            value={`${tab.label} ${tab.pageLabel} ${tab.group} ${tab.keywords || ''} tab`}
                            onSelect={() => handleSelect(`${tab.pageId}::${tab.tabValue}`)}
                            className="cursor-pointer"
                          >
                            <Icon className="mr-2 h-4 w-4 text-indigo-500" />
                            <span className="text-gray-500">{tab.pageLabel}</span>
                            <ChevronRight className="mx-1 h-3 w-3 text-gray-400" />
                            <span className="font-medium">{tab.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })}

                {/* Tabs for pages not in sidebar groups (orphan tab groups) */}
                {(() => {
                  const sidebarPageIds = new Set(Object.values(groupedPages).flat().map(p => p.id));
                  const sidebarGroupNames = new Set(Object.keys(groupedPages));
                  const orphanTabs = activeTabsSource.filter(t => !sidebarPageIds.has(t.pageId) && !sidebarGroupNames.has(t.group));
                  if (orphanTabs.length === 0) return null;
                  const orphanGroups = orphanTabs.reduce<Record<string, typeof ALL_TABS>>((acc, tab) => {
                    if (!acc[tab.group]) acc[tab.group] = [];
                    acc[tab.group].push(tab);
                    return acc;
                  }, {});
                  return Object.entries(orphanGroups).map(([groupName, tabs]) => (
                    <CommandGroup key={`orphan-${groupName}`} heading={groupName}>
                      {tabs.map(tab => {
                        const Icon = tab.icon || ArrowRight;
                        return (
                          <CommandItem
                            key={`orphan-${tab.pageId}::${tab.tabValue}`}
                            value={`${tab.label} ${tab.pageLabel} ${tab.group} ${tab.keywords || ''} tab`}
                            onSelect={() => handleSelect(`${tab.pageId}::${tab.tabValue}`)}
                            className="cursor-pointer"
                          >
                            <Icon className="mr-2 h-4 w-4 text-indigo-500" />
                            <span className="text-gray-500">{tab.pageLabel}</span>
                            <ChevronRight className="mx-1 h-3 w-3 text-gray-400" />
                            <span className="font-medium">{tab.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ));
                })()}
              </>
            )}

            {/* ── Org switcher mode ── */}
            {mode === 'orgs' && (
              <CommandGroup heading="Organizations">
                {organizations.length === 0 && orgsLoaded && (
                  <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No organizations available.
                  </div>
                )}
                {!orgsLoaded && (
                  <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading organizations...
                  </div>
                )}
                {organizations.map(org => {
                  const isActive = org.cbo_id === activeOrgId;
                  const orgName = org.organization?.name || `Organization ${org.cbo_id}`;
                  const OrgIcon = org.is_employer ? Briefcase : org.is_sponsor ? Award : Building;
                  return (
                    <CommandItem
                      key={org.cbo_id}
                      value={`${orgName} ${org.role || ''}`}
                      onSelect={() => handleOrgSwitch(org.cbo_id)}
                      className="cursor-pointer"
                    >
                      <OrgIcon className={`mr-2 h-4 w-4 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className={`text-sm ${isActive ? 'font-semibold text-blue-700 dark:text-blue-300' : ''}`}>
                          {orgName}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {org.role?.replace('_', ' ')}
                          {org.is_sponsor && org.sponsor_type ? ` \u00b7 ${org.sponsor_type.replace('_', ' ')}` : ''}
                          {org.is_employer ? ' \u00b7 Employer' : ''}
                        </span>
                      </div>
                      {isActive && <Check className="ml-2 h-4 w-4 text-blue-600 dark:text-blue-400" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
            <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-mono border border-gray-200 dark:border-gray-700">&uarr;&darr;</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-mono border border-gray-200 dark:border-gray-700">&crarr;</kbd>
                  select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-mono border border-gray-200 dark:border-gray-700">esc</kbd>
                  {mode !== 'default' ? 'back' : 'close'}
                </span>
              </div>
              <span className="text-gray-300 dark:text-gray-600">
                {modKey}+K
              </span>
            </div>
          </div>
        </Command>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default CommandPalette;
